import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import { getFormValues } from 'redux-form';

import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import values from 'lodash/values';
import range from 'lodash/range';
import orderBy from 'lodash/orderBy';

import moment from 'moment';
import qs from 'qs';

import http from '../../shared/http';
import endpoints from '../../../config/endpoints';

import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';
import { groupRowsByType } from '../../shared/thread-utils';
import createThreadsForm from '../components/create-form';
import createFilters from '../../shared/filters';
import { default as createCalendar, getDateRangeFromCalendar } from '../../shared/calendar';
import createStatusesFilter from '../components/quick-filters/statuses';
import createSwitchControlMulti from '../../shared/switch-control/multi';
import createCloseForm from '../components/close-form/for-thread-list';

const createThreadForm = createThreadsForm({
	namespace: 'threadsList',
	stateNodeName: 'createThreadForm',
	formName: 'threadsCreateThreadForm'
});

const filters = createFilters({
	namespace: 'threadsList',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [
		{
			name: 'affiliates',
			title: 'Affiliates',
			placeholder: 'Add affiliates',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATES
			}
		},
		{
			name: 'categories',
			title: 'Categories',
			placeholder: 'Add categories',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'static',
				source: 'root.lists.thread-categories'
			}
		},
		{
			name: 'managers',
			title: 'Managers',
			placeholder: 'Add managers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.ADMINS
			}
		}
	]
});

const calendar = createCalendar({
	namespace: 'threadsList',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(1, 'year').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const statusesFilter = createStatusesFilter({
	namespace: 'threadsList',
	stateNodeName: 'statusesFilter'
});

export const tableViewType = {
	group: 'group',
	list: 'list'
};

const switchControlMulti = createSwitchControlMulti({
	namespace: 'threadsList',
	stateNodeName: 'switchControlMulti',
	defaultValues: [
		{ name: 'all', placeholder: 'All', active: true },
		{ name: 'normal', placeholder: 'Normal', active: false },
		{ name: 'high', placeholder: 'High', active: false },
		{ name: 'critical', placeholder: 'Critical', active: false }
	]
});

const closeForm = createCloseForm({
	namespace: 'threadsList',
	stateNodeName: 'closeThreadsForm'
});

// actions types
const FETCH_DATA_SUCCESS = 'threads-list/fetch-data-success';
const FETCH_DATA_ERROR = 'threads-list/fetch-data-error';
const SORT_BY_COLUMN = 'threads-list/table-sort-by-column';
const TOGGLE_PROGRESS_BAR = 'threads-list/toggle-progress-bar';
const GOTO_PAGE_SUCCESS = 'threads-list/goto-page-success';

const APPLY = 'threads-list/apply';
const CLEAR = 'threads-list/clear';

const PAUSE_SUCCESS = 'threads-list/pause-success';
const UNPAUSE_SUCCESS = 'threads-list/unpause-success';
const PAUSE_UNPAUSE_ERROR = 'threads-list/pause-unpause-error';
const SWITCH_TABLE_VIEW = 'threads-list/switch-table-view';

const SET_SEARCH_QUERY = 'threads-list/set-search-query';

// permissions
export const allowedPermissions = {
	view: 'threads_list',
	create: 'threads_create',
	answer: 'threads_answer',
	closeTicket: 'threads_close',
	closeGiftOrderTicket: 'threads_gift_order_close'
};

// initial state
const initialState = {
	data: null,
	showProgressBar: false,
	createThreadForm: createThreadForm.initialState,
	filters: filters.initialState,
	calendar: calendar.initialState,
	search: '',
	statusesFilter: statusesFilter.initialState,
	pagination: {
		currentPage: 1,
		perPage: 100,
		total: null
	},
	switchControlMulti: switchControlMulti.initialState,
	table: {
		view: tableViewType.group,
		columns: {
			updated_at: {
				index: 'updated_at',
				title: 'Date',
				direction: 'desc'
			},
			id: {
				index: 'id',
				title: 'Id',
				direction: 'desc'
			},
			title: {
				index: 'title',
				title: 'Theme',
				direction: 'desc'
			},
			affiliate: {
				index: 'affiliate',
				title: 'Affiliate'
			},
			priority: {
				index: 'priority',
				title: 'Priority',
				direction: 'desc'
			},
			answered_in: {
				index: 'answered_in',
				title: 'Answered in'
			},
			answered_by: {
				index: 'answered_by',
				title: 'Answered by'
			},
			manager: {
				index: 'manager',
				title: 'Manager',
				className: 'hide-less-1600'
			},
			actions: {
				title: 'Actions'
			}
		},
		sortByColumn: {
			index: 'updated_at',
			title: 'Date',
			direction: 'desc'
		}
	},
	// need for list of ticket in thread/item module
	requestQueryString: '',
	closeThreadsForm: closeForm.initialState,
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			const { perPage } = getState().threadsList.pagination;
			const externalQuery = (props && props.location && props.location.query && qs.parse(props.location.query)) || {};
			const savedFilters = filters.selectors.getSavedFilters(getState(), 'threadsList');

			if (isEmpty(externalQuery) && savedFilters.entity && savedFilters.entity.filters.length) {
				return dispatch(replace(`threads/?${qs.stringify(savedFilters)}`));
			}

			if (document.isLoading) {
				return;
			}

			document.isLoading = true;

			return dispatch(actions.fetchRequest(0, perPage, externalQuery && externalQuery.query)).then(action => {
				document.isLoading = false;
				if (externalQuery.entity) {
					const filtersList = externalQuery.entity.filters || [];
					dispatch(filters.actions.setFiltersFromExternalQuery(filtersList));
				}
			});
		},

		fetchRequest: (offset = 0, limit = 100, externalQuery = {}) => (dispatch, getState) => {
			const { search, calendar, filters: { selectedFilters } } = getState().threadsList;

			const queryFilters = {};
			const inverseFilters = {};

			if (selectedFilters) {
				selectedFilters.forEach(filter => {
					const values = filter.value.map(f => f.value);
					if (values.length) {
						const selectedFilterValues = filter.shouldInverse ? inverseFilters : queryFilters;
						selectedFilterValues[filter.name] = values;
					}
				});
			}

			const externalQueryFilters = isEmpty(externalQuery) ? {} : {...externalQuery.queryFilters};
			const externalQueryInverseFilters = isEmpty(externalQuery) ? {} : {...externalQuery.inverseFilters};

			const query = qs.stringify({
				offset,
				limit,
				search,
				date_range: getDateRangeFromCalendar(calendar),
				quick_filters: getQuickFilters(getState().threadsList),
				...externalQuery,
				filters: {
					...queryFilters,
					externalQueryFilters
				},
				inverse_filters: {
					...inverseFilters,
					externalQueryInverseFilters
				}});

			return http({
				url: `${endpoints.THREADS.LIST}?${query}`,
				method: 'get',
				actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR],
				addToResponse: {
					requestQueryString: query
				}
			})(dispatch);
		},

		apply: () => (dispatch, getState) => {
			const searchForm = getFormValues('threadsSearchForm')(getState());
			const search = searchForm && searchForm.search;

			dispatch(actions.toggleProgressBar());

			setTimeout(
				() =>
					dispatch({
						type: APPLY,
						payload: search
					}),
				100
			);
		},

		search: () => (dispatch, getState) => {
			dispatch(actions.setSearchQuery());

			dispatch(actions.fetchRequest());
		},

		setSearchQuery: () => (dispatch, getState) => {
			const searchForm = getFormValues('threadsSearchForm')(getState());
			const search = searchForm && searchForm.search || '';

			dispatch({
				type: SET_SEARCH_QUERY,
				payload: search
			});
		},

		clear: () => (dispatch, getState) => {
			dispatch(replace('/threads'));
			dispatch({ type: CLEAR });
		},

		pause: id => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());

			http({
				url: endpoints.THREADS.PAUSE.replace('{id}', id),
				method: 'put',
				actions: [PAUSE_SUCCESS, PAUSE_UNPAUSE_ERROR]
			})(dispatch);
		},

		unpause: id => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());

			http({
				url: endpoints.THREADS.UNPAUSE.replace('{id}', id),
				method: 'put',
				actions: [UNPAUSE_SUCCESS, PAUSE_UNPAUSE_ERROR]
			})(dispatch);
		},

		sortByColumn: columnIndex => ({
			type: SORT_BY_COLUMN,
			payload: columnIndex
		}),

		redirectToModuleIndex: () => dispatch => {
			// @todo Adhock for redux-form submit functionality.
			setTimeout(() => dispatch(replace('threads/')), 100);
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		}),

		gotoPage: page => (dispatch, getState) => {
			const { perPage } = getState().threadsList.pagination;
			const offset = (page - 1) * perPage;

			dispatch(actions.toggleProgressBar());

			dispatch(actions.fetchRequest(offset, perPage, null)).then(action => {
				if (action.type === FETCH_DATA_SUCCESS) {
					dispatch({
						type: GOTO_PAGE_SUCCESS,
						payload: page
					});
				}
			});
		},

		switchTableView: viewType => ({
			type: SWITCH_TABLE_VIEW,
			payload: viewType
		}),

		removeFilter: filterName => dispatch => {
			dispatch(filters.actions.clearFilterValue(filterName));
			dispatch(filters.actions.deleteFilterByName(filterName));
		},

		setFilterValues: (filterName, valuesList) => dispatch => {
			dispatch(actions.removeFilter(filterName));
			dispatch(filters.actions.addFilter(filterName, false));
			dispatch(filters.actions.addFilterValue(valuesList, filterName));
		}
	},
	createThreadForm.actions,
	filters.actions,
	calendar.actions,
	statusesFilter.actions,
	switchControlMulti.actions,
	closeForm.actions
);

// reducers
const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			if ( action.payload.pathname.indexOf('threads') !== -1) {
				return {
					...state,
					data: null
				};
			}

			return initialState;
		},
		[FETCH_DATA_SUCCESS]: (state, action) => {
			return {
				...state,
				data: action.payload.data.rows,
				pagination: {
					...state.pagination,
					total: action.payload.data.total
				},
				requestQueryString: action.meta.requestQueryString,
				showProgressBar: false,
				error: false
			};
		},
		[GOTO_PAGE_SUCCESS]: (state, action) => ({
			...state,
			pagination: {
				...state.pagination,
				currentPage: action.payload
			}
		}),
		[APPLY]: (state, action) => ({ ...state, data: null, showProgressBar: false, search: action.payload }),
		[CLEAR]: state => ({ ...initialState }),
		[PAUSE_SUCCESS]: state => ({ ...state, data: null, showProgressBar: false }),
		[UNPAUSE_SUCCESS]: state => ({ ...state, data: null, showProgressBar: false }),
		[PAUSE_UNPAUSE_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[SORT_BY_COLUMN]: (state, action) => {
			const { sortByColumn, columns } = state.table;
			const columnIndex = action.payload;
			const direction =
				sortByColumn['index'] == columnIndex
					? sortByColumn.direction === 'desc' ? 'asc' : 'desc'
					: columns[columnIndex].direction;
			return {
				...state,
				table: {
					...state.table,
					sortByColumn: {
						...columns[columnIndex],
						direction
					}
				}
			};
		},
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[SWITCH_TABLE_VIEW]: (state, action) => ({
			...state,
			table: {
				...state.table,
				view: tableViewType[action.payload]
			}
		}),
		[SET_SEARCH_QUERY]: (state, action) => ({ ...state, search: action.payload })
	},
	createThreadForm.reducers,
	filters.reducers,
	calendar.reducers,
	statusesFilter.reducers,
	switchControlMulti.reducers,
	closeForm.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.threadsList.data,
	getTable: state => {
		const { table, data } = state.threadsList;
		const rows = data ? sortColumns(data, table.sortByColumn) : [];

		let tableData = rows.map(row => ({
			...row,
			priority: find(state.root.lists['thread-priorities'], { id: row.priority }).text
		}));

		if (table.view === tableViewType.group) {
			tableData = groupRowsByType(tableData, 'threads');
		}

		return {
			headers: values(table.columns),
			rows: tableData,
			sortedColumn: table.sortByColumn,
			view: table.view
		};
	},
	getPagination: state => {
		const { perPage, total, currentPage } = state.threadsList.pagination;
		return {
			pages: range(Math.ceil(total / perPage)),
			currentPage,
			total
		};
	},
	getSearchData: state => ({ search: state.threadsList.search }),
	getCalendar: state => calendar.selectors.getCalendar(state => state.threadsList.calendar, state => state)(state),
	getFilters: state => filters.selectors.getFilters(
		state => state.threadsList.filters,
		state => state
	)(state),
	getQuickFilters: state => state.threadsList.quickFilters,
	getIntl: state => intl(state),
	getProgressBar: state => state.threadsList.showProgressBar,
	getThreadStatuses: state => state.root.lists['thread-statuses'],
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.threadsList.error,
	getPrioritiesList: createThreadForm.selectors.getPrioritiesList,
	getShouldShowCreateThreadFormStatus: createThreadForm.selectors.getShouldShowCreateThreadFormStatus,
	getAttachmentsInfo: createThreadForm.selectors.getAttachmentsInfo,
	getCreateThreadFormInitialValues: createThreadForm.selectors.getInitialValues,
	getCreateThreadFormDisabledFields: createThreadForm.selectors.getDisabledFields,
	getStatusesFilters: statusesFilter.selectors.getFilters,
	getSwitchControlMultiItems: state => switchControlMulti.selectors.getControls(state),
	getCloseFormInitialValues: closeForm.selectors.getInitialValues,
	getCloseFormShouldShowStatus: closeForm.selectors.getShouldShowCloseFormStatus,
	getCloseFormValidate: closeForm.selectors.getCloseFormValidate,
	getCloseFormCurrentThreadId: closeForm.selectors.getCloseFormCurrentThreadId,
	getRequestQueryString: state => `${state.threadsList.requestQueryString}&tableView=${state.threadsList.table.view}`
};

// functions
const sortColumns = (rows, sortedColumn) => {
	if (!rows) return null;
	return orderBy(
		rows,
		row => {
			const value = row[sortedColumn.index];
			switch (sortedColumn.index) {
				case 'updated_at':
					return moment(value).valueOf();
				default:
					return value;
			}
		},
		sortedColumn.direction
	);
};

const getQuickFilters = ({ statusesFilter, switchControlMulti}) => {
	const prepareFiltersFn = filterList =>
		filterList
			.filter(entity => entity.active)
			.map(entity => entity.name);

	return {
		statuses: prepareFiltersFn(statusesFilter['filters']),
		priorities: prepareFiltersFn(switchControlMulti['filters'])
	};
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/threads/list/index.js